import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { donationActions } from '../../../store/actions';
import { DONATION_IMPACT_TYPES, GENERAL_EDUCATION_PRODUCT_ID } from '../../../constants';
import { useEffect, useState } from 'react';
import { CauseAmountInput } from '../../../components/CauseAmountInput';
// import { FundraiserProductCard } from '../../../components/FundraiserProductCard'; // TODO: re-enable when product cards come back
import { displayError } from '../../../helpers';
import { useNonInitialEffect } from '../../../hooks/UseNonInitialEffect';
import { Alert } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function ({ state, handleChange, editView, validationErrors }) {
	const products = useSelector((state) => state.fundraiser_page.products) || [];
	const locationDetails = useSelector((state) => state.fundraiser_page.location_details);
	const countriesWithCurrencies = useSelector((state) => state.countries.countries_with_currencies);
	const currencyList = Array.isArray(countriesWithCurrencies) ? countriesWithCurrencies : [];

	const [isLoadingProducts, setIsLoadingProducts] = useState(products.length === 0);
	const dispatch = useDispatch();

	useNonInitialEffect(() => {
		setIsLoadingProducts(true);
	}, [state.selectedCurrency.countryId]);

	useEffect(() => {
		setIsLoadingProducts(false);
	}, [products]);

	useNonInitialEffect(() => {
		if (!editView && locationDetails?.is_exist && state.selectedCurrency.id !== locationDetails.currency_id) {
			handleChange(locationDetails, true);
		}
	}, [locationDetails]);

	/**
	 * PRODUCT CARDS TEMPORARILY DISABLED
	 * While cards are hidden, always default to General Education (id: 0) on creation,
	 * so the amount-only input renders instead of the stepper.
	 * Only runs when creating (not editView) and nothing is selected yet.
	 */
	useEffect(() => {
		if (!editView && (state.selectedProductId === null || state.selectedProductId === undefined)) {
			handleChange(
				{
					productId: GENERAL_EDUCATION_PRODUCT_ID,
					quantity: 1,
					goal: state.goal,
				},
				false,
				true,
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editView]);

	const selectedProduct = products.find((p) => p.id === state.selectedProductId);
	const isCustomSelected = state.selectedProductId === GENERAL_EDUCATION_PRODUCT_ID;
	const selectedRate = selectedProduct ? Number(selectedProduct.rates?.yearly) || 0 : 0;

	// Selecting a NEW product resets quantity to 1.
	// Re-clicking the ALREADY selected product keeps the current quantity.
	// NOTE: not currently reachable while product cards are hidden — kept for when they're re-enabled.
	const selectProduct = (product) => {
		const isCustom = product.id === GENERAL_EDUCATION_PRODUCT_ID;
		const rate = Number(product.rates?.yearly) || 0;
		const isAlreadySelected = state.selectedProductId === product.id;
		const nextQuantity = isAlreadySelected ? state.productQuantity || 1 : 1;

		handleChange(
			{
				productId: product.id,
				quantity: nextQuantity,
				goal: isCustom ? state.goal : nextQuantity * rate,
			},
			false,
			true,
		);
	};

	const changeQuantity = (delta) => {
		const nextQty = Math.max(1, (state.productQuantity || 1) + delta);
		handleChange(
			{
				productId: state.selectedProductId,
				quantity: nextQty,
				goal: nextQty * selectedRate,
			},
			false,
			true,
		);
	};

	const getCurrencySymbol = (currencyCode) => {
		const match = currencyList.find((c) => c.currency_code === currencyCode);
		return match?.symbol_left || currencyCode || '';
	};

	const selectedCurrencySymbol = currencyList.find((c) => c.id === state.selectedCurrency.countryId)?.symbol_left || '';

	useNonInitialEffect(() => {
		if (state.selectedProductId === GENERAL_EDUCATION_PRODUCT_ID) {
			const calculateImpact = setTimeout(() => {
				dispatch(donationActions.calculateProductImpact(state.selectedCurrency.id, state.goal, DONATION_IMPACT_TYPES.annual));
			}, 500);

			return () => clearTimeout(calculateImpact);
		}
	}, [state.goal, state.selectedCurrency, state.selectedProductId]);

	return (
		<Stack gap={4} className="ChooseCauseTab">
			<Alert severity="error" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={{ borderRadius: '8px' }}>
				All funds raised go directly to TCF to support children’s education and build schools.
			</Alert>
			<Typography className="ChooseCauseLabel" variant="bold">
				{/* What would you like to fundraise for? */}
				Enter the amount you would like to raise for children’s education.
			</Typography>

			{/*
				PRODUCT CARDS TEMPORARILY HIDDEN
				Re-enable by uncommenting this block and the FundraiserProductCard import above.
				Also remove (or gate) the default-select useEffect above once cards are back,
				since users should be able to pick a product again instead of always landing on General Education.

			<Box className="ProductGrid">
				{products.map((product) => (
					<FundraiserProductCard
						key={product.id}
						product={product}
						isSelected={state.selectedProductId === product.id}
						isCustom={product.id === GENERAL_EDUCATION_PRODUCT_ID}
						isLoadingProducts={isLoadingProducts}
						currencySymbol={getCurrencySymbol(product.currency)}
						onSelect={selectProduct}
					/>
				))}
			</Box>
			*/}

			{isCustomSelected || state.selectedProductId === null ? (
				<CauseAmountInput
					mode="amount"
					selectedCurrencyOption={state.selectedCurrency.name}
					currencyChangeHandler={(currency) => handleChange(currency, true)}
					disableCurrencyEdit={editView}
					currencySymbol={selectedCurrencySymbol}
					amountProps={{
						id: 'goal',
						name: 'goal',
						placeholder: '0',
						value: state.goal,
						onChange: handleChange,
						maxLength: '15',
						error: displayError(validationErrors.goal),
						helpertext: validationErrors.goal,
					}}
				/>
			) : (
				<CauseAmountInput
					mode="stepper"
					selectedCurrencyOption={state.selectedCurrency.name}
					currencyChangeHandler={(currency) => handleChange(currency, true)}
					disableCurrencyEdit={editView}
					quantity={state.productQuantity}
					onIncrement={() => changeQuantity(1)}
					onDecrement={() => changeQuantity(-1)}
					unitRate={selectedRate}
					currencyCode={getCurrencySymbol(selectedProduct?.currency)}
					productName={selectedProduct?.public_name}
				/>
			)}
		</Stack>
	);
}